import React, { useEffect, useRef, useState, useCallback } from 'react';
import io, { Socket } from 'socket.io-client';
import { useSearchParams } from 'react-router-dom';
import './App.css';
import PICTIONARY_WORDS from './pictionaryWords';

// Get API and Socket.IO URLs from environment variables
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
const SHOW_WEB_GAME = process.env.REACT_APP_SHOW_WEB_GAME === 'Yes';

// Types
interface Message {
  id: number;
  player?: string;
  playerName?: string;
  text: string;
  type: 'guess' | 'correct' | 'system';
}

interface DrawPosition {
  x: number;
  y: number;
}

interface PlayerInfo {
  id: string;
  name: string;
  isDrawer: boolean;
  isAdmin: boolean;
  role: string;
  score: number;
}

interface GameConfig {
  roundTime: number;
  maxRounds: number;
  currentRound: number;
  configMode: boolean;
  timerValue: number | null;
}

function App() {
  // URL params for Slack integration
  const [searchParams] = useSearchParams();
  const [slackData, setSlackData] = useState<{
    roomId: string;
    slackUserName: string;
    adminName: string;
  } | null>(null);
  const [isSlackVerified, setIsSlackVerified] = useState<boolean>(false);
  const [slackError, setSlackError] = useState<string | null>(null);
  // Add specific error handling states
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [isAuthFailed, setIsAuthFailed] = useState<boolean>(false);
  
  // Socket state
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  
  // Room state
  const [roomId, setRoomId] = useState<string>('');
  const [isInRoom, setIsInRoom] = useState<boolean>(false);
  const [players, setPlayers] = useState<string[]>([]);
  const [playerInfo, setPlayerInfo] = useState<PlayerInfo[]>([]);
  const [scores, setScores] = useState<Map<string, number>>(new Map());
  const [adminName, setAdminName] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  
  // Game state
  const [isDrawer, setIsDrawer] = useState<boolean>(false);
  const [currentWord, setCurrentWord] = useState<string | null>(null);
  const [guess, setGuess] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [hasGuessedCorrectly, setHasGuessedCorrectly] = useState<boolean>(false);
  
  // Game configuration
  const [gameConfig, setGameConfig] = useState<GameConfig>({
    roundTime: 60,
    maxRounds: 3,
    currentRound: 0,
    configMode: true,
    timerValue: null
  });
  const [configRoundTime, setConfigRoundTime] = useState<number>(60);
  const [configMaxRounds, setConfigMaxRounds] = useState<number>(3);
  
  // UI state
  const [roomError, setRoomError] = useState<string | null>(null);
  const [gameMessage, setGameMessage] = useState<string | null>(null);
  
  // Drawing state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const lastPositionRef = useRef<DrawPosition | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [drawColor, setDrawColor] = useState<string>('#000000');
  
  // Near line 47, with other state variables
  const [isSlackLoading, setIsSlackLoading] = useState<boolean>(false);
  
  // Initialize socket connection
  useEffect(() => {
    // Create socket connection using environment variable
    const newSocket = io(SOCKET_URL);
    setSocket(newSocket);
    
    // Socket event handlers
    newSocket.on('connect', () => {
      console.log('Connected to server');
      setIsConnected(true);
    });
    
    newSocket.on('disconnect', () => {
      console.log('Disconnected from socket server');
      setIsConnected(false);
      setIsInRoom(false);
    });
    
    return () => {
      newSocket.disconnect();
    };
  }, []);
  
  // Process the Slack URL parameter
  useEffect(() => {
    // Get the raw search string (e.g., "?ref=xxxxx")
    const search = window.location.search;
    
    // Extract the ref parameter without decoding
    const refMatch = search.match(/[?&]ref=([^&]*)/);
    if (!refMatch) return;
    
    // Get the encoded value
    const encodedRef = refMatch[1];
    
    // Now manually decode it once
    const refParam = decodeURIComponent(encodedRef);
    
    console.log('Raw encoded ref:', encodedRef);
    console.log('Properly decoded ref:', refParam);
    
    // Set loading state to true when we detect a Slack ref
    setIsSlackLoading(true);
    
    // Verify the Slack reference with the server
    const verifySlackRef = async () => {
      try {
        console.log('trying to make api call to verify slack')
        console.log(refParam)
        const response = await fetch(`${API_URL}/api/verify-slack`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ encryptedData: refParam }),
        });
        
        const data = await response.json();
        
        if (data.success) {
          console.log('Slack verification successful', data);
          setSlackData({
            roomId: data.roomId,
            slackUserName: data.slackUserName,
            adminName: data.adminName,
          });
          setRoomId(data.roomId);
          setIsSlackVerified(true);
          setSlackError(null);
          setIsSessionExpired(false);
          setIsAuthFailed(false);
          // Loading continues until we join
        } else {
          console.error('Slack verification failed:', data.message);
          
          // Turn off loading for all error cases
          setIsSlackLoading(false);
          
          // Handle specific error cases
          if (data.message === 'Game session has expired') {
            setIsSessionExpired(true);
            setIsAuthFailed(false);
          } else {
            setIsAuthFailed(true);
            setIsSessionExpired(false);
          }
          
          setSlackError(data.message || 'Failed to verify Slack game link');
          setIsSlackVerified(false);
        }
      } catch (error) {
        console.error('Error verifying Slack reference:', error);
        
        // Also turn off loading when there's an error
        setIsSlackLoading(false);
        
        setSlackError('Failed to connect to the server');
        setIsSlackVerified(false);
        setIsAuthFailed(true);
        setIsSessionExpired(false);
      }
    };
    
    verifySlackRef();
  }, []);
  
  // Auto-join room from Slack when socket is connected
  useEffect(() => {
    if (!socket || !isConnected || !isSlackVerified || !slackData) return;
    
    console.log('Auto-joining from Slack with data:', slackData);
    
    // Check if the room exists first by trying to join
    socket.emit('joinRoom', {
      roomId: slackData.roomId,
      slackUserName: slackData.slackUserName
    });
    
    // Set up a one-time listener for room success
    const handleRoomJoined = () => {
      // Turn off loading when successfully joined
      setIsSlackLoading(false);
    };
    
    // Set up a one-time listener for room errors
    const handleRoomError = (error: string) => {
      console.log('Room error, attempting to create room instead:', error);
      
      // Room doesn't exist, create it
      socket.emit('createRoom', {
        roomId: slackData.roomId,
        slackUserName: slackData.slackUserName,
        adminName: slackData.adminName
      });
      
      // Remove this listener after first use
      socket.off('roomError', handleRoomError);
    };
    
    socket.on('roomError', handleRoomError);
    socket.on('playerJoined', handleRoomJoined);
    socket.on('roomCreated', handleRoomJoined);
    
    // Cleanup listener if component unmounts
    return () => {
      socket.off('roomError', handleRoomError);
      socket.off('playerJoined', handleRoomJoined);
      socket.off('roomCreated', handleRoomJoined);
    };
  }, [socket, isConnected, isSlackVerified, slackData]);
  
  // Redirect to Slack function
  const redirectToSlack = () => {
    window.location.href = 'https://app.slack.com';
  };
  
  // Handle drawing data from other players
  const handleDrawData = useCallback((drawData: { start: DrawPosition, end: DrawPosition, isDot?: boolean, color?: string }) => {
    if (isDrawer) return;
    
    const ctx = canvasContextRef.current;
    if (!ctx) return;
    
    // Set drawing style based on received data
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    
    // Use received color or default to black
    const color = drawData.color || '#000000';
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    
    if (drawData.isDot) {
      // Draw dot
      ctx.beginPath();
      ctx.arc(drawData.start.x, drawData.start.y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Draw line
      ctx.beginPath();
      ctx.moveTo(drawData.start.x, drawData.start.y);
      ctx.lineTo(drawData.end.x, drawData.end.y);
      ctx.stroke();
    }
  }, [isDrawer]);
  
  // Clear canvas function
  const clearCanvas = useCallback((notifyServer: boolean = true) => {
    const ctx = canvasContextRef.current;
    const canvas = canvasRef.current;
    
    if (!ctx || !canvas) return;
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Reset style to current drawing color
    ctx.strokeStyle = drawColor;
    ctx.fillStyle = drawColor;
    
    // Notify other players if requested
    if (notifyServer && socket && isDrawer) {
      socket.emit('clearCanvas', { roomId });
    }
  }, [drawColor, socket, isDrawer, roomId]);
  
  // Setup socket event listeners for room management
  useEffect(() => {
    if (!socket) return;
    
    // Room creation success
    socket.on('roomCreated', (createdRoomId: string) => {
      console.log('Room created:', createdRoomId);
      setRoomId(createdRoomId);
      setIsInRoom(true);
      setRoomError(null);
      showGameMessage('Room created successfully!');
    });
    
    // Player joined update
    socket.on('playerJoined', ({ players, playerInfo, scores, adminName, admin, gameConfig, canvasData }: { 
      players: string[], 
      playerInfo: PlayerInfo[],
      scores: [string, number][],
      adminName: string | null,
      admin: string,
      gameConfig: GameConfig,
      canvasData: any[]
    }) => {
      console.log('Player joined event:', { players, playerInfo, scores, adminName, admin, gameConfig, canvasData });
      setPlayers(players);
      setPlayerInfo(playerInfo || []);
      setScores(new Map(scores));
      setAdminName(adminName);
      setIsAdmin(admin === socket.id);
      setGameConfig(gameConfig || {
        roundTime: 60,
        maxRounds: 3,
        currentRound: 0,
        configMode: true,
        timerValue: null
      });
      setConfigRoundTime(gameConfig && gameConfig.roundTime ? gameConfig.roundTime : 60);
      setConfigMaxRounds(gameConfig && gameConfig.maxRounds ? gameConfig.maxRounds : 3);
      setIsInRoom(true);
      setRoomError(null);
      
      // Replay canvas data if available
      if (canvasData && canvasData.length > 0) {
        replayCanvasData(canvasData);
      }
    });
    
    // Game state update
    socket.on('gameState', ({ scores, playerInfo, messages, adminName, admin, gameConfig, canvasData }: { 
      scores: [string, number][], 
      playerInfo: PlayerInfo[],
      messages: Message[],
      adminName: string | null,
      admin: string,
      gameConfig: GameConfig,
      canvasData: any[]
    }) => {
      console.log('Game state update');
      setScores(new Map(scores));
      setPlayerInfo(playerInfo || []);
      setMessages(messages);
      setAdminName(adminName);
      setIsAdmin(admin === socket.id);
      setGameConfig(gameConfig || {
        roundTime: 60,
        maxRounds: 3,
        currentRound: 0,
        configMode: true,
        timerValue: null
      });
      
      // Update timer if provided
      if (gameConfig && gameConfig.timerValue !== undefined && gameConfig.timerValue !== null) {
        setTimeLeft(gameConfig.timerValue);
      }
      
      // Replay canvas data if available and we're not the drawer
      if (canvasData && canvasData.length > 0 && !isDrawer) {
        replayCanvasData(canvasData);
      }
    });
    
    // Timer update
    socket.on('timerUpdate', ({ time }: { time: number }) => {
      setTimeLeft(time);
    });
    
    // Permission error
    socket.on('permissionError', (error: string) => {
      console.error('Permission error:', error);
      showGameMessage(`Permission error: ${error}`);
    });
    
    // Configuration error
    socket.on('configError', (error: string) => {
      console.error('Configuration error:', error);
      showGameMessage(`Configuration error: ${error}`);
    });
    
    // Chat update
    socket.on('chatUpdate', ({ messages }: { messages: Message[] }) => {
      console.log('Chat update received');
      setMessages(messages);
    });
    
    // Room error
    socket.on('roomError', (error: string) => {
      console.error('Room error:', error);
      setRoomError(error);
    });
    
    // Drawing update
    socket.on('playerDraw', (data: { roomId: string, drawData: any }) => {
      console.log('Received drawing data');
      handleDrawData(data.drawData);
    });
    
    // Canvas cleared
    socket.on('canvasCleared', () => {
      console.log('Canvas cleared by drawer');
      clearCanvas(false);
    });
    
    // Drawer selected
    socket.on('drawerSelected', ({ drawer }: { drawer: string }) => {
      console.log('Drawer selected:', drawer, 'current ID:', socket.id);
      const isCurrentUserDrawer = drawer === socket.id;
      setIsDrawer(isCurrentUserDrawer);
      
      // Clear canvas for new round/game
      clearCanvas(false);
      
      if (isCurrentUserDrawer) {
        const randomWord = PICTIONARY_WORDS[Math.floor(Math.random() * PICTIONARY_WORDS.length)];
        setCurrentWord(randomWord);
        socket.emit('setWord', { roomId, word: randomWord });
        showGameMessage(`You are the drawer! Your word is: ${randomWord}`);
      } else {
        setCurrentWord(null);
        showGameMessage('New round started! Get ready to guess!');
      }
      
      // Reset the hasGuessedCorrectly flag for new round
      setHasGuessedCorrectly(false);
    });
    
    // Correct guess
    socket.on('correctGuess', ({ player, playerName, scores, pointsEarned }: { 
      player: string, 
      playerName: string, 
      scores: [string, number][],
      pointsEarned: number 
    }) => {
      console.log('Correct guess by:', player, 'Points earned:', pointsEarned);
      setScores(new Map(scores));
      
      if (player === socket.id) {
        setHasGuessedCorrectly(true);
        showGameMessage(`Congratulations! You guessed correctly! +${pointsEarned} points`);
      } else {
        showGameMessage(`${playerName} guessed correctly! +${pointsEarned} points`);
      }
    });
    
    // Scores reset
    socket.on('scoresReset', ({ scores }: { scores: [string, number][] }) => {
      console.log('Scores reset');
      setScores(new Map(scores));
      showGameMessage('Game has been reset!');
    });
    
    // Game ended
    socket.on('gameEnded', () => {
      console.log('Game ended, clearing current word and drawer status');
      setCurrentWord(null);
      setIsDrawer(false);
      setHasGuessedCorrectly(false);
      
      // If we were in drawing mode, stop it
      if (isDrawing) {
        setIsDrawing(false);
        lastPositionRef.current = null;
      }
    });
    
    return () => {
      // Remove all listeners
      socket.off('roomCreated');
      socket.off('playerJoined');
      socket.off('gameState');
      socket.off('chatUpdate');
      socket.off('roomError');
      socket.off('playerDraw');
      socket.off('canvasCleared');
      socket.off('drawerSelected');
      socket.off('correctGuess');
      socket.off('scoresReset');
      socket.off('timerUpdate');
      socket.off('permissionError');
      socket.off('configError');
      socket.off('gameEnded');
    };
  }, [socket, roomId, handleDrawData, clearCanvas, isDrawer]);
  
  // Initialize canvas (only when in a room)
  useEffect(() => {
    if (!isInRoom) return; // Only initialize when in room
    
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Canvas ref is null');
      return;
    }
    
    console.log('Initializing canvas...');
    
    // Set canvas dimensions
    canvas.width = 800;
    canvas.height = 600;
    
    // Get context and set initial properties
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }
    
    // Set drawing properties
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    
    // Fill canvas with white initially
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Reset to default color
    ctx.strokeStyle = drawColor;
    ctx.fillStyle = drawColor;
    
    // Store context in ref
    canvasContextRef.current = ctx;
    
    console.log('Canvas initialized successfully');
  }, [isInRoom]); // Remove drawColor from dependency array to prevent reinitializing
  
  // Update canvas color when drawColor changes without reinitializing
  useEffect(() => {
    const ctx = canvasContextRef.current;
    if (!ctx || !isInRoom) return;
    
    // Update drawing colors without clearing the canvas
    ctx.strokeStyle = drawColor;
    ctx.fillStyle = drawColor;
    
    console.log('Updated canvas color to:', drawColor);
  }, [drawColor, isInRoom]);
  
  // Show temporary game message
  const showGameMessage = (message: string) => {
    setGameMessage(message);
    setTimeout(() => setGameMessage(null), 5000);
  };
  
  // Room management functions
  const handleCreateRoom = () => {
    if (!socket || !roomId.trim()) {
      if (!roomId.trim()) {
        setRoomError('Please enter a room ID');
      }
      return;
    }
    
    console.log('Creating room:', roomId);
    socket.emit('createRoom', roomId);
  };
  
  const handleJoinRoom = () => {
    if (!socket || !roomId.trim()) {
      if (!roomId.trim()) {
        setRoomError('Please enter a room ID');
      }
      return;
    }
    
    console.log('Joining room:', roomId);
    socket.emit('joinRoom', roomId);
  };
  
  const handleBecomeDrawer = () => {
    if (!socket || !isInRoom) return;
    
    console.log('Becoming drawer in room:', roomId);
    socket.emit('becomeDrawer', { roomId });
  };
  
  const handleResetGame = () => {
    if (!socket || !isInRoom) return;
    
    console.log(`Resetting game in room:`, roomId);
    socket.emit('resetGame', { roomId });
    clearCanvas(true);
  };
  
  // Function to update game configuration
  const handleUpdateConfig = () => {
    if (!socket || !isInRoom || !isAdmin) return;
    
    console.log('Updating game configuration:', { roundTime: configRoundTime, maxRounds: configMaxRounds });
    socket.emit('updateGameConfig', {
      roomId,
      config: {
        roundTime: configRoundTime,
        maxRounds: configMaxRounds
      }
    });
  };
  
  // Function to start the game (update settings and become drawer)
  const handleStartGame = () => {
    if (!socket || !isInRoom || !isAdmin) return;
    
    // Update the game configuration and start the game in one step
    console.log('Starting game with settings:', { roundTime: configRoundTime, maxRounds: configMaxRounds });
    socket.emit('updateGameConfig', {
      roomId,
      config: {
        roundTime: configRoundTime,
        maxRounds: configMaxRounds
      },
      startGame: true
    });
  };
  
  // Drawing functions
  const getMousePosition = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): DrawPosition => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Canvas ref is null while getting mouse position');
      return { x: 0, y: 0 };
    }
    
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    // Calculate position with scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  }, []);
  
  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawer) return;
    
    // Prevent default touch behavior (scrolling)
    if ('touches' in e) {
      e.preventDefault();
    }
    
    const position = getMousePosition(e);
    console.log('Starting drawing at position:', position);
    
    // Set drawing flag
    setIsDrawing(true);
    
    // Record starting position
    lastPositionRef.current = position;
    
    // Draw a dot at the starting position
    const ctx = canvasContextRef.current;
    if (ctx) {
      ctx.beginPath();
      ctx.arc(position.x, position.y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      
      // Send dot to server
      if (socket) {
        socket.emit('draw', {
          roomId,
          drawData: {
            start: position,
            end: position,
            isDot: true,
            color: drawColor
          }
        });
      }
    }
  }, [isDrawer, getMousePosition, drawColor, roomId, socket]);
  
  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !isDrawer || !lastPositionRef.current) return;
    
    // Prevent default touch behavior
    if ('touches' in e) {
      e.preventDefault();
    }
    
    const currentPosition = getMousePosition(e);
    
    const ctx = canvasContextRef.current;
    if (ctx) {
      // Draw line from last position to current position
      ctx.beginPath();
      ctx.moveTo(lastPositionRef.current.x, lastPositionRef.current.y);
      ctx.lineTo(currentPosition.x, currentPosition.y);
      ctx.stroke();
      
      // Send line to server
      if (socket) {
        socket.emit('draw', {
          roomId,
          drawData: {
            start: lastPositionRef.current,
            end: currentPosition,
            color: drawColor
          }
        });
      }
      
      // Update last position
      lastPositionRef.current = currentPosition;
    }
  }, [isDrawing, isDrawer, getMousePosition, drawColor, roomId, socket]);
  
  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
    lastPositionRef.current = null;
  }, []);
  
  // Guessing function
  const handleGuessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!socket || !isInRoom || isDrawer || !guess.trim()) return;
    
    console.log('Submitting guess:', guess);
    socket.emit('guess', { roomId, guess: guess.trim() });
    setGuess('');
  };
  
  // Add touch event handlers for mobile devices
  useEffect(() => {
    if (!isInRoom) return;
    
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('No canvas for touch events');
      return;
    }
    
    console.log('Setting up touch event handlers');
    
    // Function handlers for touch events
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      startDrawing(e as unknown as React.TouchEvent<HTMLCanvasElement>);
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      draw(e as unknown as React.TouchEvent<HTMLCanvasElement>);
    };
    
    const handleTouchEnd = () => {
      stopDrawing();
    };
    
    // Add touch event listeners
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    
    // Cleanup function
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isInRoom, isDrawer, startDrawing, draw, stopDrawing]);
  
  // Add this new function to replay canvas data
  const replayCanvasData = useCallback((canvasData: any[]) => {
    const ctx = canvasContextRef.current;
    if (!ctx || !canvasData || canvasData.length === 0) return;

    console.log('Replaying canvas data:', canvasData.length, 'drawing actions');

    // Clear canvas first
    const canvas = canvasRef.current;
    if (canvas) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Replay each drawing action
    canvasData.forEach((drawData: any) => {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 3;

      const color = drawData.color || '#000000';
      ctx.strokeStyle = color;
      ctx.fillStyle = color;

      if (drawData.isDot) {
        // Draw dot
        ctx.beginPath();
        ctx.arc(drawData.start.x, drawData.start.y, ctx.lineWidth / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Draw line
        ctx.beginPath();
        ctx.moveTo(drawData.start.x, drawData.start.y);
        ctx.lineTo(drawData.end.x, drawData.end.y);
        ctx.stroke();
      }
    });

    // Reset to current drawing color
    ctx.strokeStyle = drawColor;
    ctx.fillStyle = drawColor;
  }, [drawColor]);
  
  // Main rendering
  return (
    <div className="app-container">
      {/* Game messages */}
      {gameMessage && (
        <div className="game-message">{gameMessage}</div>
      )}
      
      {/* Session expired error message */}
      {isSessionExpired && (
        <div className="error-message session-expired">
          <div className="error-header">
            <h3>Game Session Expired</h3>
          </div>
          <div className="error-content">
            <p>Game session has expired. Please start a new game from Slack.</p>
            <button onClick={redirectToSlack} className="redirect-button">
              Return to Slack
            </button>
          </div>
        </div>
      )}
      
      {/* Authentication failed error message */}
      {isAuthFailed && (
        <div className="error-message auth-failed">
          <div className="error-header">
            <h3>Authentication Failed</h3>
          </div>
          <div className="error-content">
            <p>Authentication failed. Please try with a valid URL.</p>
          </div>
        </div>
      )}
      
      {/* Regular error messages */}
      {roomError && !isSessionExpired && !isAuthFailed && (
        <div className="error-message">{roomError}</div>
      )}
      
      {/* Regular Slack error messages (for non-specific errors) */}
      {slackError && !isSessionExpired && !isAuthFailed && (
        <div className="error-message slack-error">
          <h3>Slack Integration Error</h3>
          <p>{slackError}</p>
          <p>Please try joining the game manually below.</p>
        </div>
      )}
      
      {isSlackLoading ? (
        // Loading screen for Slack users
        <div className="slack-loading">
          <h1>Pictionary</h1>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2>Connecting to game...</h2>
            <p>Please wait while we set up your game session</p>
          </div>
        </div>
      ) : !isInRoom && !isSessionExpired && !isAuthFailed ? (
        SHOW_WEB_GAME ? (
          // Room joining controls
          <div className="room-controls">
            <h1>Pictionary</h1>
            <div className="input-group">
              <input
                type="text"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                placeholder="Enter room ID"
                className="room-input"
              />
              <div className="button-group">
                <button onClick={handleCreateRoom} className="create-button">Create Room</button>
                <button onClick={handleJoinRoom} className="join-button">Join Room</button>
              </div>
            </div>
            <div className="connection-status">
              {isConnected ? (
                <span className="connected">Connected to server</span>
              ) : (
                <span className="disconnected">Disconnected from server</span>
              )}
            </div>
            {slackData && (
              <div className="slack-info">
                <p>Joining from Slack as: <strong>{slackData.slackUserName}</strong></p>
                <p>Room: <strong>{slackData.roomId}</strong></p>
              </div>
            )}
          </div>
        ) : (
          // Show message to use Slack instead
          <div className="slack-only-message">
            <h1>Pictionary</h1>
            <div className="message-container">
              <h2>Please start the game from Slack</h2>
              <p>This game is only available through the Slack integration.</p>
              <button onClick={redirectToSlack} className="redirect-button">
                Go to Slack
              </button>
            </div>
            <div className="connection-status">
              {isConnected ? (
                <span className="connected">Connected to server</span>
              ) : (
                <span className="disconnected">Disconnected from server</span>
              )}
            </div>
          </div>
        )
      ) : isInRoom ? (
        // Game interface
        <div className="game-container">
          {/* Game info sidebar */}
          <div className="game-info">
            <h2 className="room-id">Room: {roomId}</h2>
            
            <div className="players-list">
              <h3>Players</h3>
              <ul>
                {playerInfo.map((player) => {
                  const isCurrentPlayer = player.id === (socket ? socket.id : '');
                  
                  return (
                    <li key={player.id} className={isCurrentPlayer ? 'current-player' : ''}>
                      {isCurrentPlayer ? 'You' : player.name}
                      {player.isDrawer && ' (Drawer)'}
                      {player.isAdmin && ' (Admin)'}
                      <span className="score">Score: {player.score}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            
            {/* Configuration Panel (only visible in configMode and for admin) */}
            {gameConfig.configMode && isAdmin && (
              <div className="config-panel">
                <h3>Game Settings</h3>
                
                <div className="config-option">
                  <label>Round Time:</label>
                  <select 
                    value={configRoundTime} 
                    onChange={(e) => setConfigRoundTime(Number(e.target.value))}
                  >
                    <option value={30}>30 seconds</option>
                    <option value={60}>60 seconds</option>
                    <option value={90}>90 seconds</option>
                    <option value={120}>120 seconds</option>
                  </select>
                </div>
                
                <div className="config-option">
                  <label>Rounds per Player:</label>
                  <select 
                    value={configMaxRounds} 
                    onChange={(e) => setConfigMaxRounds(Number(e.target.value))}
                  >
                    <option value={1}>1 round</option>
                    <option value={2}>2 rounds</option>
                    <option value={3}>3 rounds</option>
                    <option value={5}>5 rounds</option>
                  </select>
                </div>
                
                <button 
                  onClick={handleStartGame}
                  className="start-game-button"
                >
                  Start Game
                </button>
              </div>
            )}
            
            {/* Game Timer (only visible when game is active) */}
            {timeLeft !== null && !gameConfig.configMode && (
              <div className={`game-timer ${timeLeft <= 10 ? 'urgent' : ''}`}>
                <h3>Time Remaining</h3>
                <div className="timer-value">{timeLeft}</div>
              </div>
            )}
            
            {/* Round Information */}
            {!gameConfig.configMode && (
              <div className="round-info">
                <h3>Game Progress</h3>
                <p>Round {gameConfig.currentRound} of {gameConfig.maxRounds * playerInfo.length}</p>
              </div>
            )}
            
            <div className="game-controls">
              {/* Become Drawer button - only visible in config mode or when no drawer exists */}
              {!isDrawer && gameConfig.configMode && (
                <button 
                  onClick={handleBecomeDrawer}
                  className="become-drawer-button"
                >
                  Become Drawer
                </button>
              )}
              
              {/* New Game button - only for admin */}
              {isAdmin && (
                <button 
                  onClick={handleResetGame}
                  className="reset-button"
                >
                  New Game
                </button>
              )}
            </div>
            
            {isDrawer && currentWord && (
              <div className="current-word">
                Current word: <strong>{currentWord}</strong>
              </div>
            )}
            
            {adminName && (
              <div className="admin-info">
                Game created by: <strong>{adminName}</strong>
              </div>
            )}
          </div>
          
          {/* Drawing canvas */}
          <div className="canvas-container">
            <canvas
              ref={canvasRef}
              className="drawing-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              style={{ cursor: isDrawer ? 'crosshair' : 'default' }}
            />
            
            {/* Drawing controls */}
            {isDrawer && (
              <div className="drawing-controls">
                <div className="color-picker">
                  <input
                    type="color"
                    value={drawColor}
                    onChange={(e) => setDrawColor(e.target.value)}
                  />
                </div>
                <button 
                  onClick={() => clearCanvas(true)}
                  className="clear-button"
                >
                  Clear Canvas
                </button>
              </div>
            )}
          </div>
          
          {/* Chat and guessing */}
          <div className="chat-container">
            <div className="messages">
              {messages.map((message) => (
                <div key={message.id} className={`message ${message.type}`}>
                  {message.player === (socket ? socket.id : '') ? (
                    <span className="player-name">
                      You:
                    </span>
                  ) : message.player ? (
                    <span className="player-name">
                      {message.playerName || `Player ${message.player && typeof message.player === 'string' ? message.player.slice(0, 4) : 'Unknown'}`}:
                    </span>
                  ) : null}
                  <span className="message-text">{message.text}</span>
                </div>
              ))}
            </div>
            
            {!isDrawer && (
              <form onSubmit={handleGuessSubmit} className="guess-form">
                <input
                  type="text"
                  value={guess}
                  onChange={(e) => setGuess(e.target.value)}
                  placeholder={hasGuessedCorrectly ? "You've already guessed correctly!" : "Type your guess here..."}
                  disabled={isDrawer || hasGuessedCorrectly}
                />
                <button 
                  type="submit" 
                  disabled={isDrawer || hasGuessedCorrectly}
                >
                  Guess
                </button>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App; 